<template>
  <AppCard class="w-full h-full" :top-padding="topPadding" :bottom-padding="bottomPadding">
    <!-- Header -->
    <template #header>
      <div class="toolbar flex justify-between">
        <div>Crop Image</div>
      </div>
    </template>

    <!-- Main cropper -->
    <template #default>
      <div class="touch-none w-full h-full">
        <Cropper v-if="isTouchOnly" ref="cropperRef" class="cropper" backgroundClass="cropper-background"
          foregroundClass="cropper-foreground" :src="src" :stencil-component="stencil" :stencil-props="stencilProps"
          :image-restriction="'stencil'" :resizeImage="{ adjustStencil: false }" :default-size="getDefaultSize"
          @change="onCropChange" @ready="onReady" />
        <Cropper v-else ref="cropperRef" class="cropper" backgroundClass="cropper-background"
          foregroundClass="cropper-foreground" :src="src" :stencil-component="stencil" :stencil-props="stencilProps"
          :image-restriction="'fit-area'" :resizeImage="{ adjustStencil: false }" :default-size="getDefaultSize"
          @change="onCropChange" @ready="onReady" />
      </div>
    </template>
    <!-- Footer -->
    <template #footer>
      <div class="flex justify-center">
        <div class="flex flex-wrap content-center gap-1.5">
          <app-btn v-for="ratio in ratios" :key="ratio.label" button-style="secondary"
            :is-disabled="aspectRatio !== ratio.value" @click="setRatio(ratio.value)">
            {{ ratio.label }}
          </app-btn>
          <app-btn @click="onPreview">Preview</app-btn>
          <app-btn @click="emit('cancel')">Cancel</app-btn>
        </div>
      </div>
    </template>
  </AppCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/theme.bubble.css';
import { useIsTouchOnly } from '@/composables/useIsTouchOnly'
import type { Coordinates, ImageTransforms, VisibleArea, ImageSize } from 'vue-advanced-cropper'
import CustomStencil from './CustomStencil.ce.vue'
import type { OverlayData } from '@/composables/useOverlay';
import AppCard from './AppCard.vue';
import AppBtn from './AppBtn.vue';

const { isTouchOnly } = useIsTouchOnly()

const props = withDefaults(defineProps<{
  src: string
  defaultAspect?: number,
  overlayData: OverlayData
  bottomPadding?: number
  topPadding?: number
}>(), {
  defaultAspect: 4 / 5,
  topPadding: 0,
  bottomPadding: 0
})
const emit = defineEmits(['preview', 'cancel', 'change', 'ready'])

const cropperRef = ref<InstanceType<typeof Cropper> | null>(null)
const aspectRatio = ref(props.defaultAspect)

const ratios = [
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '9:16', value: 9 / 16 },
  // { label: '16:9', value: 16 / 9 }
]


const stencilProps = computed(() => {
  return {
    aspectRatio: aspectRatio.value,
    handlers: isTouchOnly.value ? {} : undefined,
    movable: !isTouchOnly.value,
    resizable: !isTouchOnly.value,
    overlay: showOverlay.value,
    overlayData: props.overlayData
  }
})


function onReady() {
  console.log("==== cropper is ready")
  emit('ready')
}

function manualRefresh() {
  console.log('manualRefresh called', cropperRef.value ? 'has Ref' : 'no Ref')
  if (cropperRef.value) {
    cropperRef.value.refresh();
  }
}

function manualReset() {
  console.log('manualReset called', cropperRef.value ? 'has Ref' : 'no Ref')
  if (cropperRef.value) {
    cropperRef.value.reset()
  }
}

// EXPOSE the function
defineExpose({
  manualRefresh,
  manualReset
});

// Define an interface for the function argument
interface DefaultSizeArguments {
  visibleArea: VisibleArea;
  imageSize: ImageSize;
  stencilRatio: { minimum: number, maximum: number },
  sizeRestrictions: { minWidth: number, minHeight: number, maxWidth: number, maxHeight: number }
}
const getDefaultSize = ({ imageSize, visibleArea }: DefaultSizeArguments) => {
  console.log('getDefaultSize', imageSize, visibleArea)
  return {
    width: (visibleArea || imageSize).width,
    height: (visibleArea || imageSize).height,
  };
}

onMounted(() => {
  console.log('imageCropper  onMounted', props.bottomPadding)
  if (cropperRef.value) {
    cropperRef.value.reset()
  }

})

async function setRatio(newRatio: number) {
  aspectRatio.value = newRatio
  if (cropperRef.value) {
    cropperRef.value.reset()
  }
}

export interface CropperEvent {
  coordinates: Coordinates;
  visibleArea: VisibleArea;
  canvas?: HTMLCanvasElement;
  image: {
    width: number;
    height: number;
    transforms: ImageTransforms;
    src: string | null;
  };
};

function onCropChange(e: CropperEvent) {
  emit('change', e)
}

const stencil = CustomStencil
// const stencil = RectangleStencil

const showOverlay = ref(true)

function onPreview() {
  try {
    const result = cropperRef.value?.getResult()
    if (result) {
      console.log('onPreview', result)
      emit('preview', result)
    }
  } catch (error) {
    console.log('onPreview', error)
  }
}
</script>

<style scoped>
.cropper {
  width: 100%;
  height: 100%;
  object-fit: contain;
  touch-action: none;
  background-color: white;
}

:deep(.cropper-foreground) {
  background: white;
}

:deep(.cropper-background) {
  background: white;
}
</style>
